import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TrendingUp, Play, LogOut, ClipboardCheck, CheckCircle, ArrowLeft } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import UserAssessment from '@/components/UserAssessment';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentData, setAssessmentData] = useState(() => {
    // Initialize from localStorage synchronously
    const savedAssessment = localStorage.getItem('userAssessment');
    return savedAssessment ? JSON.parse(savedAssessment) : null;
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleStartSession = () => {
    navigate('/client/session');
  };

  const handleStartAssessment = () => {
    setShowAssessment(true);
  };

  const handleAssessmentComplete = (result) => {
    setAssessmentData(result);
    // Save to localStorage for persistence
    localStorage.setItem('userAssessment', JSON.stringify(result));
    setShowAssessment(false);
  };

  // Show assessment component if active
  if (showAssessment) {
    return (
      <div className="min-h-screen bg-ivory-mist text-obsidian-night">
        {/* Header */}
        <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div>
              <h1 className="font-display text-3xl font-bold text-obsidian-night">GNTL STRNGTH</h1>
              <p className="text-sm text-shadow-pine">Archetype Assessment</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowAssessment(false)}
              className="text-shadow-pine hover:text-burnt-terra hover:bg-burnt-terra/10"
            >
              Cancel
            </Button>
          </div>
        </header>
        
        <div className="container mx-auto px-4 py-8">
          <UserAssessment 
            onComplete={handleAssessmentComplete} 
            existingAssessment={assessmentData}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-mist text-obsidian-night" data-testid="client-dashboard">
      {/* Header */}
      <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/client/hub')}
              className="text-shadow-pine hover:text-burnt-terra hover:bg-burnt-terra/10 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-bold text-obsidian-night">Physical Architecture</h1>
              <p className="text-sm text-shadow-pine">The Grid • {user?.name || 'User'}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-shadow-pine hover:text-burnt-terra hover:bg-burnt-terra/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Main Session Card */}
        <Card className="bg-gradient-to-br from-obsidian-night to-shadow-pine border-burnt-terra/30 text-ivory-mist shadow-xl" data-testid="start-session-card">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-ivory-mist">Daily Calibration</CardTitle>
            <CardDescription className="text-ivory-mist/70 font-body text-lg">
              15 minutes • Grid-guided movement • 5-minute reflection
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-ivory-mist/80 font-body">
              Select your tools and begin your calibration session. Each session includes 
              a 30-second preparation, 15 minutes of guided movement, and 5 minutes of journaling.
            </p>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-white/10 rounded-lg">
                <div className="font-bold text-ivory-mist text-xl">30 sec</div>
                <div className="text-ivory-mist/60 font-body text-sm">Prepare</div>
              </div>
              <div className="p-3 bg-burnt-terra/30 rounded-lg">
                <div className="font-bold text-ivory-mist text-xl">15 min</div>
                <div className="text-ivory-mist/60 font-body text-sm">Workout</div>
              </div>
              <div className="p-3 bg-white/10 rounded-lg">
                <div className="font-bold text-ivory-mist text-xl">5 min</div>
                <div className="text-ivory-mist/60 font-body text-sm">Journal</div>
              </div>
            </div>

            <Button
              onClick={handleStartSession}
              size="lg"
              className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold text-lg py-6"
              data-testid="start-session-button"
            >
              <Play className="w-6 h-6 mr-2" />
              Start Session
            </Button>
          </CardContent>
        </Card>

        {/* Assessment Card */}
        <Card className={`border-2 shadow-lg ${assessmentData ? 'bg-shadow-pine/10 border-shadow-pine/30' : 'bg-white border-burnt-terra/20'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {assessmentData ? (
                  <CheckCircle className="w-6 h-6 text-shadow-pine" />
                ) : (
                  <ClipboardCheck className="w-6 h-6 text-burnt-terra" />
                )}
                <div>
                  <CardTitle className="font-display text-obsidian-night">
                    Pattern Assessment
                  </CardTitle>
                  <CardDescription className="text-shadow-pine font-body">
                    {assessmentData 
                      ? `Completed • ${assessmentData.handedness}-handed • ${assessmentData.twistDirection} twist pattern`
                      : 'Customize your sessions based on your movement patterns'
                    }
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {assessmentData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-shadow-pine/60 font-body">Opposite Leg</div>
                    <div className="font-semibold text-shadow-pine">{assessmentData.oppositeLeg}</div>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-shadow-pine/60 font-body">Opposite Lunge</div>
                    <div className="font-semibold text-shadow-pine">{assessmentData.oppositeSideLunge}</div>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <div className="text-shadow-pine/60 font-body">Lead Arm</div>
                    <div className="font-semibold text-shadow-pine">{assessmentData.leadArm}</div>
                  </div>
                </div>
                <Button
                  onClick={handleStartAssessment}
                  variant="outline"
                  className="w-full border-shadow-pine/40 text-shadow-pine hover:bg-shadow-pine/20"
                >
                  Retake Assessment
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-shadow-pine/70 font-body text-sm">
                  Take a quick 2-minute assessment to identify your natural movement patterns. 
                  We will use this to challenge you with opposite positions during prep and break phases.
                </p>
                <Button
                  onClick={handleStartAssessment}
                  className="w-full bg-burnt-terra hover:bg-burnt-terra/90 text-white"
                >
                  <ClipboardCheck className="w-5 h-5 mr-2" />
                  Take Assessment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-1 gap-4">
          <Button
            onClick={() => navigate('/client/progress')}
            className="h-20 bg-white hover:bg-shadow-pine hover:text-ivory-mist border-2 border-shadow-pine/30 text-obsidian-night transition-all"
            data-testid="view-progress-button"
          >
            <TrendingUp className="w-5 h-5 mr-2" />
            View Progress
          </Button>
        </div>

        {/* Session Info */}
        <Card className="bg-white border-burnt-terra/20 shadow-lg">
          <CardHeader>
            <CardTitle className="font-display text-obsidian-night">How It Works</CardTitle>
            <CardDescription className="text-shadow-pine font-body">
              Each calibration session follows this flow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-shadow-pine/5 rounded-lg">
                <div className="w-8 h-8 bg-shadow-pine/20 rounded-full flex items-center justify-center text-shadow-pine font-bold">1</div>
                <div>
                  <h4 className="font-semibold text-obsidian-night font-body">Select Tools</h4>
                  <p className="text-sm text-shadow-pine/70 font-body">
                    Choose one or more tools: Empty Hand, Mace, Long Bar, or Rope. 
                    If you select Rope, those exercises will appear in the final cycles.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-burnt-terra/5 rounded-lg">
                <div className="w-8 h-8 bg-burnt-terra/20 rounded-full flex items-center justify-center text-burnt-terra font-bold">2</div>
                <div>
                  <h4 className="font-semibold text-obsidian-night font-body">Follow the Grid</h4>
                  <p className="text-sm text-shadow-pine/70 font-body">
                    Movement exercises are assigned with random chamber and level positions. 
                    Hand and leg configurations guide your form during breaks.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-shadow-pine/5 rounded-lg">
                <div className="w-8 h-8 bg-shadow-pine/20 rounded-full flex items-center justify-center text-shadow-pine font-bold">3</div>
                <div>
                  <h4 className="font-semibold text-obsidian-night font-body">Reflect & Journal</h4>
                  <p className="text-sm text-shadow-pine/70 font-body">
                    After the workout, spend 5 minutes journaling with guided prompts 
                    to integrate your experience.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <BrandMark />
    </div>
  );
};

export default ClientDashboard;
